import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { useQueryClient } from '@tanstack/react-query';
import { getRouteApi } from '@tanstack/react-router';
import { useSetAtom } from 'jotai';
import { ChevronUp, CopyIcon, PlusIcon, TrashIcon } from 'lucide-react';
import { canWrite } from '@/features/permissions/resource-permissions';

import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { projectAtom } from '@/features/navigation/atoms/projectAtom';
import { getNumberFormatter } from '@/utils/getNumberFormatter';
import { parseSearchParamsToSearchQuery } from '@/utils/parseSearchParamsToSearchQuery';
import type { SortByValues } from '@/utils/sortTypes';
import { cn } from '@/lib/utils';

import { useDeleteEstate } from '../estates/api/estates';
import { ProjectEstatesInfiniteScroll } from '../estates/components/project-estates-infinite-scroll';
import { Toolbar } from '../estates/components/toolbar';
import { EstateImage } from '../estates-property/components/estate-image';
import { getFloorLabel } from '../estates-property/utils/floorLabelHandler';
import {
  getProjectEstatesOptions,
  type ProjectEstates,
  useProjectEstates,
} from '../projects/api/project-estates';
import type { Project } from '../projects/api/projects';
import { useModal } from '@/components/modals/new/use-modal';
import { ConfirmDeleteModal } from '@/components/modals/confirm-delete-modal';
import { CreateProjectObjectModal } from '../../components/modals/create-project-object-modal';
import { CopyProjectObjectModal } from '@/components/modals/copy-project-object-modal';
import { EstateAvailabilityBadge } from '../estates/components/estate-availability-badge';

const url = '/_authenticated/_sidebar-layout/projects/$projectId/_dashboard-layout/objects';
const route = getRouteApi(url);

type ObjectsTableProps = {
  project: Project;
};

type SortState = {
  by?: SortByValues;
  order?: 'asc' | 'desc';
};

type SortableTableHeadProps = {
  field: SortByValues;
  currentSort?: SortState;
  children: React.ReactNode;
  className?: string;
  onSort: (field: SortByValues) => void;
};

const SortableTableHead = ({
  field,
  currentSort,
  children,
  className,
  onSort,
}: SortableTableHeadProps) => {
  const isActive = currentSort?.by === field;
  const isDesc = isActive && currentSort?.order === 'desc';

  return (
    <TableHead className={className}>
      <button
        type="button"
        onClick={() => onSort(field)}
        className="flex items-center gap-1 text-left text-primary-dark transition-colors hover:text-primary"
      >
        <span>{children}</span>
        {isActive ? <ChevronUp size={12} className={cn(isDesc && 'rotate-180')} /> : null}
      </button>
    </TableHead>
  );
};

export const ObjectsTable: React.FC<ObjectsTableProps> = ({ project }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const navigate = route.useNavigate();
  const { filters, sort } = route.useSearch();
  const currentSort = sort as SortState | undefined;
  const parsedFilters = parseSearchParamsToSearchQuery(filters);
  const modal = useModal();
  const copyObjectModal = useModal();
  const [copyEstateId, setCopyEstateId] = useState<string>();
  const canWriteGeneral = canWrite(project.metadata?.permissions, 'general');

  const setProject = useSetAtom(projectAtom);

  const sortQuery = useMemo(
    () => (sort?.by && sort.order ? { by: sort.by, order: sort.order } : undefined),
    [sort]
  );

  const queryProjectEstatesOptions = useMemo(
    () => ({
      projectId: project?.id,
      query: {
        ...(Object.keys(parsedFilters).length ? { filters: parsedFilters } : {}),
        ...(sortQuery ? { sort: sortQuery } : {}),
      },
    }),
    [project, parsedFilters, sortQuery]
  );

  const { data } = useProjectEstates(queryProjectEstatesOptions);

  const { mutate: deleteEstate } = useDeleteEstate({
    mutationConfig: {
      onSuccess: () => {
        toast.success(t('deleteEstateSuccess'));
        queryClient.invalidateQueries({
          queryKey: getProjectEstatesOptions(queryProjectEstatesOptions).queryKey,
        });
      },
      onError: (err) => {
        toast.error(err.message);
      },
    },
  });

  const { estates, totalEstates } = useMemo(
    () => ({
      estates: data.estates.data,
      totalEstates: data.count ?? 0,
    }),
    [data]
  );

  const invalidateProjectEstates = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: getProjectEstatesOptions(queryProjectEstatesOptions).queryKey,
    });
  }, [queryClient, queryProjectEstatesOptions]);

  const navigateToEstatePage = useCallback(
    (id: string) => {
      if (project?.id && project?.name) {
        setProject({ id: project.id, name: project.name });
      } else {
        setProject(null);
      }
      navigate({ to: '/estates/$estateId', params: { estateId: id } });
    },
    [navigate, project?.id, project?.name, setProject]
  );

  const handleCopyClick = (estateId: string) => {
    if (!canWriteGeneral) {
      return;
    }

    setCopyEstateId(estateId);
    copyObjectModal.open();
  };

  const onAddObjectButtonClick = useCallback(() => {
    if (!canWriteGeneral) {
      return;
    }

    modal.open();
  }, [canWriteGeneral, modal]);

  const handleDeleteEstate = (estateId: string) => {
    if (!canWriteGeneral) {
      return;
    }

    deleteEstate(estateId);
  };

  const handleSort = useCallback(
    (field: SortByValues) => {
      navigate({
        search: (prev) => {
          const prevSort = prev.sort as SortState | undefined;

          let nextSort: { by: SortByValues; order: 'asc' | 'desc' } | undefined;

          if (prevSort?.by === field) {
            if (prevSort.order === 'asc') {
              nextSort = { by: field, order: 'desc' };
            } else if (prevSort.order === 'desc') {
              nextSort = undefined;
            } else {
              nextSort = { by: field, order: 'asc' };
            }
          } else {
            nextSort = { by: field, order: 'asc' };
          }

          if (!nextSort) {
            const { sort: _, ...rest } = prev;
            return rest;
          }

          return {
            ...prev,
            sort: nextSort,
          };
        },
      });
    },
    [navigate]
  );

  const buttonAddObject = useMemo(
    () => (
      <>
        <Button
          size="xl"
          variant="outline"
          className="flex justify-center gap-[5px] px-[20px] lg:px-10 xl:px-3"
          onClick={onAddObjectButtonClick}
        >
          <PlusIcon />
          <span className="block max-xl:hidden">{t('add-new-estate')}</span>
        </Button>
        <CreateProjectObjectModal
          modal={modal}
          navigate={navigate}
          project={{
            id: project?.id || '',
            name: project?.name || '',
          }}
          onSuccess={() => {
            invalidateProjectEstates();
          }}
        />
      </>
    ),
    [
      invalidateProjectEstates,
      modal,
      navigate,
      onAddObjectButtonClick,
      project?.id,
      project?.name,
      t,
    ]
  );

  return (
    <>
      <div className="flex size-full flex-col gap-4">
        <Toolbar
          url={url}
          showViewSwitch={false}
          rightButtonAction={canWriteGeneral ? buttonAddObject : undefined}
        />
        <Card className="px-8 py-6 lg:px-20">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-md text-primary-dark">{project?.name}</h2>
              <h5 className="text-sm text-text-secondary">
                {totalEstates} {t('linked-objects-in-totals')}
              </h5>
            </div>
          </div>

          {estates.length ? (
            <Table className="my-5 w-full table-auto bg-transparent [&_thead_tr]:border-b-0">
              <TableHeader className="h-11 rounded-[10px] bg-[#EDEDED] max-sm:hidden">
                <TableRow className="w-full justify-between rounded-[10px]">
                  <TableHead className="rounded-bl-[10px] rounded-tl-[10px] text-primary-dark">
                    {t('thumbnail')}
                  </TableHead>
                  <TableHead className="text-primary-dark">{t('external-id')}</TableHead>
                  <SortableTableHead
                    field="name"
                    currentSort={currentSort}
                    onSort={handleSort}
                    className="text-primary-dark"
                  >
                    {t('object-name')}
                  </SortableTableHead>
                  <TableHead className="text-primary-dark">{t('floor')}</TableHead>
                  <SortableTableHead
                    field="rooms"
                    currentSort={currentSort}
                    onSort={handleSort}
                    className="text-primary-dark"
                  >
                    {t('rooms')}
                  </SortableTableHead>
                  <SortableTableHead
                    field="area"
                    currentSort={currentSort}
                    onSort={handleSort}
                    className="text-primary-dark"
                  >
                    {t('area')}
                  </SortableTableHead>
                  <SortableTableHead
                    field="price"
                    currentSort={currentSort}
                    onSort={handleSort}
                    className="text-primary-dark"
                  >
                    {t('price')}
                  </SortableTableHead>
                  <TableHead className="text-primary-dark">{t('availability')}</TableHead>
                  <TableHead className="text-primary-dark">{t('status')}</TableHead>
                  <TableHead className="rounded-br-[10px] rounded-tr-[10px] text-primary-dark" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {estates.map((estate) => (
                  <TableRow
                    key={`project-object-${estate.id}`}
                    className="cursor-pointer items-center text-primary-dark"
                    onClick={() => {
                      navigateToEstatePage(estate.id);
                    }}
                  >
                    <EstateTableRow
                      estate={estate}
                      handleCopyClick={handleCopyClick}
                      handleDeleteEstate={handleDeleteEstate}
                      canWriteGeneral={canWriteGeneral}
                    />
                  </TableRow>
                ))}
                <ProjectEstatesInfiniteScroll
                  projectId={project?.id}
                  filters={filters}
                  sort={sortQuery}
                  navigateToEstatePage={navigateToEstatePage}
                  rowRenderer={(estate) => (
                    <EstateTableRow
                      key={`project-object-${estate.id}`}
                      estate={estate}
                      handleCopyClick={handleCopyClick}
                      handleDeleteEstate={handleDeleteEstate}
                      canWriteGeneral={canWriteGeneral}
                    />
                  )}
                />
              </TableBody>
            </Table>
          ) : (
            <div className="flex justify-center py-16">
              <span>{t('no-estates')}</span>
            </div>
          )}
        </Card>
      </div>
      {copyEstateId ? (
        <CopyProjectObjectModal
          modal={copyObjectModal}
          estateId={copyEstateId}
          onSubmit={() => {
            queryClient.invalidateQueries({
              queryKey: getProjectEstatesOptions(queryProjectEstatesOptions).queryKey,
            });
          }}
          onSuccess={() => {
            invalidateProjectEstates();
          }}
          navigate={navigate}
        />
      ) : null}
    </>
  );
};

const EstateTableRow: React.FC<{
  estate: ProjectEstates[number];
  handleCopyClick: (estateId: string) => void;
  handleDeleteEstate: (estateId: string) => void;
  canWriteGeneral: boolean;
}> = ({ estate, handleCopyClick, handleDeleteEstate, canWriteGeneral }) => {
  const { t } = useTranslation();
  const deleteModal = useModal();
  return (
    <>
      {/* Thumbnail */}
      <TableCell className="whitespace-normal break-words py-5 max-sm:hidden">
        <EstateImage
          className="h-16 w-28 rounded"
          thumbnailId={estate.thumbnail?.mediaId}
          iconSize={28}
        />
      </TableCell>
      {/* External ID */}
      <TableCell className="max-w-40 whitespace-normal break-words py-5 max-sm:hidden">
        {estate.estateDetails?.externalId}
      </TableCell>
      {/* Object name */}
      <TableCell className="max-w-40 whitespace-normal break-words py-5 max-sm:hidden">
        {estate.name}
      </TableCell>
      {/* Floor */}
      <TableCell className="max-w-40 whitespace-normal break-words py-5 uppercase max-sm:hidden">
        {typeof estate.estateAreasRooms?.floor === 'number'
          ? getFloorLabel(estate.estateAreasRooms?.floor)
          : ''}
      </TableCell>
      {/* Rooms */}
      <TableCell className="max-w-40 whitespace-normal break-words py-5 capitalize max-sm:hidden">
        {estate.estateAreasRooms?.totalNumberOfRooms
          ? `${getNumberFormatter(estate.estateAreasRooms?.totalNumberOfRooms, 'de-DE', {
              maximumFractionDigits: 1,
              minimumFractionDigits: 1,
            })} ${t('rooms')}`
          : ''}
      </TableCell>
      {/* Area */}
      <TableCell className="max-w-40 whitespace-normal break-words py-5 max-sm:hidden">
        {estate.estateAreasRooms?.livingArea
          ? `${getNumberFormatter(estate.estateAreasRooms?.livingArea, 'de-DE')}${t('m2')}`
          : ''}
      </TableCell>
      {/* Price */}
      <TableCell className="max-w-40 whitespace-normal break-words py-5 max-sm:hidden">
        {estate.prices?.buyingPrice
          ? getNumberFormatter(estate.prices?.buyingPrice, 'de-DE', {
              maximumFractionDigits: 1,
              minimumFractionDigits: 1,
            })
          : ''}
      </TableCell>
      {/* Availability */}
      <TableCell className="max-w-40 whitespace-normal break-words py-5 max-sm:hidden">
        <EstateAvailabilityBadge availability={estate.availability ?? null} />
      </TableCell>
      {/* Status */}
      <TableCell className="max-w-40 whitespace-normal break-words py-5 capitalize max-sm:hidden">
        {estate.status}
      </TableCell>
      {/* Link */}
      <TableCell className="max-w-40 whitespace-normal break-words py-5 max-sm:hidden">
        {canWriteGeneral ? (
          <div className="flex gap-2">
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-7 w-7 border-primary-light text-primary-light"
              onClick={(ev) => {
                ev.stopPropagation();
                deleteModal.open();
              }}
            >
              <TrashIcon />
            </Button>
            <Button
              type="button"
              onClick={(ev) => {
                ev.stopPropagation();
                handleCopyClick(estate.id);
              }}
              size="sm"
              variant="outline"
              className="h-7 w-7 border-primary-light text-primary-light"
            >
              <CopyIcon />
            </Button>
          </div>
        ) : null}
      </TableCell>
      {canWriteGeneral ? (
        <ConfirmDeleteModal
          modal={deleteModal}
          onSubmit={() => handleDeleteEstate(estate.id)}
          title={t('deleteEstate')}
          description={t('confirmDeleteEstate')}
        />
      ) : null}
    </>
  );
};
